import React, { useEffect, useState, useRef } from 'react';
import { DatePicker } from 'antd';
import { useRemirrorContext } from '@remirror/react';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';

const DatePickerShortcut = ({ open, triggerToggleDatePicker }) => {
  const { view } = useRemirrorContext();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const dateRef = useRef(dayjs()); // Stores the date without re-rendering
  const inputRef = useRef(null);
  const lastModifiedFieldRef = useRef('day'); // Track last edited field
  const cursorPosRef = useRef(0);

  useEffect(() => {
    const handleFocus = () => {
      inputRef.current = document.querySelector('.ant-picker-input input'); // Get DatePicker's internal input
    };

    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);

  useEffect(() => {
    const inputElement = document.querySelector('.ant-picker-input input');
    if (!inputElement) return;

    setTimeout(() => {
      if (inputElement) {
        inputElement.focus();
        let newCursorPos = 2; // Default to year
        if (lastModifiedFieldRef.current === 'month') newCursorPos = 5;
        else if (lastModifiedFieldRef.current === 'day') newCursorPos = 8;
        inputElement.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 10);
  }, [selectedDate]);

  const handleKeyDown = (e) => {
    if (!['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) return;

    const inputElement = document.querySelector('.ant-picker-input input');
    if (!inputElement) return;

    const cursorPos = inputElement.selectionStart;
    cursorPosRef.current = cursorPos; // Save cursor position

    let newDate = dayjs(dateRef.current);

    if (e.key === 'Enter' || e.key === 'Click') {
      setSelectedDate(newDate);
      handleDateSelect(newDate);
    } else {
      if (cursorPos <= 4) {
        newDate = newDate.add(e.key === 'ArrowUp' ? 1 : -1, 'year');
        lastModifiedFieldRef.current = 'year';
      } else if (cursorPos <= 7) {
        newDate = newDate.add(e.key === 'ArrowUp' ? 1 : -1, 'month');
        lastModifiedFieldRef.current = 'month';
      } else {
        newDate = newDate.add(e.key === 'ArrowUp' ? 1 : -1, 'day');
        lastModifiedFieldRef.current = 'day';
      }
      dateRef.current = newDate;
      setSelectedDate(dayjs(newDate)); // Ensure UI updates
    }

    setTimeout(() => {
      if (inputElement) {
        inputElement.focus();
        let newCursorPos = 2; // Default to year
        if (lastModifiedFieldRef.current === 'month') newCursorPos = 5;
        else if (lastModifiedFieldRef.current === 'day') newCursorPos = 8;
        inputElement.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    e.preventDefault();
  };

  useEffect(() => {
    const handleDateClick = (event) => {
      const target = event.target.closest('.inserted-date');

      const dateLabels = ['Date', 'Add date'];
      const isDateShortcut =
        dateLabels.includes(event.target.innerHTML) || dateLabels.includes(event.target.lastChild?.textContent);

      if (!target && !isDateShortcut) {
        triggerToggleDatePicker({ flag: false, from: 0, to: 0 });
        return;
      }

      const pos = view.posAtDOM(target, 0);
      const dateText = target?.textContent?.trim();

      if (dayjs(dateText, 'MMM D, YYYY', true).isValid()) {
        const parsedDate = dayjs(dateText, 'MMM D, YYYY');
        setSelectedDate(parsedDate);
        dateRef.current = parsedDate;
        triggerToggleDatePicker({
          flag: true,
          from: pos,
          to: pos + dateText.length,
          selectedDate: parsedDate,
        });
      }
    };

    document.addEventListener('click', handleDateClick);
    return () => document.removeEventListener('click', handleDateClick);
  }, [view, triggerToggleDatePicker]);

  const datePickerPosition = open ? getPosition() : { left: 0, top: 0 };

  function getPosition() {
    const editorRect = view.dom.getBoundingClientRect();
    const coords = view.coordsAtPos(open.from);
    return {
      left: coords.left - editorRect.left,
      top: coords.top - editorRect.top + 100,
    };
  }

  const handleDateSelect = (date) => {
    if (!date || !dayjs(date).isValid()) return;

    const formattedDate = dayjs(date).format('MMM D, YYYY');
    const sanitizedDate = DOMPurify.sanitize(formattedDate);

    const { state, dispatch } = view;
    const { schema } = state;
    const textStyleMark = schema.marks.textStyle;

    if (!textStyleMark) {
      console.error('textStyle mark is missing from schema.');
      return;
    }

    let tr = state.tr;

    if (open.to > open.from) {
      tr.replaceWith(
        open.from,
        open.to,
        schema.text(sanitizedDate, [textStyleMark.create({ class: 'inserted-date' })])
      );
    } else {
      tr.insertText(sanitizedDate, open.from);
      tr.addMark(open.from, open.from + sanitizedDate.length, textStyleMark.create({ class: 'inserted-date' }));
    }

    dispatch(tr);
    view.focus();
    triggerToggleDatePicker({ flag: false, from: 0, to: 0 });
  };

  return (
    <>
      {open.flag && (
        <div
          onClick={(e) => {
            if (e.target.nodeName === 'INPUT') {
              e.stopPropagation();
            }
          }}
          style={{
            position: 'absolute',
            left: datePickerPosition.left,
            top: datePickerPosition.top,
            zIndex: 999,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            padding: '5px',
          }}
        >
          <DatePicker
            value={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              handleDateSelect(date);
              dateRef.current = date;
            }}
            autoFocus
            open
            ref={inputRef}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}
    </>
  );
};

export default DatePickerShortcut;
